"use client"

import { useEffect, useRef, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"
import { X, PanelLeft, PanelRight, PanelBottom, ExternalLink, ChevronUp, ChevronDown, Maximize2, Minimize2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { useAnalyticsWidgetsStore } from "@/store/analytics-widgets-store"

interface AnalyticsWidgetShellProps {
  id: string
  title: string
  onClose: () => void
  defaultWidth?: number
  defaultHeight?: number
  fitContent?: boolean
  children: ReactNode
}

function DockBtn({ title, onClick, children }: { title: string; onClick: (e: React.MouseEvent) => void; children: ReactNode }) {
  return (
    <button
      title={title}
      onClick={onClick}
      className="flex items-center justify-center w-5 h-5 text-muted-foreground hover:text-foreground transition-colors"
    >
      {children}
    </button>
  )
}

export function AnalyticsWidgetShell({
  id,
  title,
  onClose,
  defaultWidth = 420,
  defaultHeight = 320,
  fitContent = false,
  children,
}: AnalyticsWidgetShellProps) {
  const storageKey = `algoglide-widget-pos:${id}`
  const { docked, setDock, reorderWidget } = useAnalyticsWidgetsStore()

  const [viewport, setViewport] = useState<'mobile' | 'tablet' | 'desktop'>('desktop')
  useEffect(() => {
    const check = () => {
      const w = window.innerWidth
      setViewport(w < 640 ? 'mobile' : w < 1400 ? 'tablet' : 'desktop')
    }
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])
  const canDockLeft = viewport === 'desktop'
  const canDockRight = viewport !== 'mobile'

  const [mounted, setMounted] = useState(false)
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: 120, y: 80 })

  // Initialize position from localStorage after mount (avoids SSR mismatch)
  useEffect(() => {
    const w = Math.min(defaultWidth, window.innerWidth - 16)
    const centered = {
      x: Math.max(8, (window.innerWidth - w) / 2),
      y: Math.max(60, (window.innerHeight - defaultHeight) / 2),
    }
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const p = JSON.parse(saved)
        // Validate saved position is actually on-screen; reset to center if not
        const onScreen = p.x >= 0 && p.x < window.innerWidth - 40 && p.y >= 0 && p.y < window.innerHeight - 40
        setPos(onScreen ? p : centered)
      } else {
        setPos(centered)
      }
    } catch {
      setPos(centered)
    }
    setMounted(true)
  }, [storageKey, id, defaultWidth, defaultHeight])

  const isDragging = useRef(false)
  const dragOffset = useRef({ x: 0, y: 0 })
  const posRef = useRef(pos)
  posRef.current = pos

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isDragging.current) return
      setPos({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      })
    }
    const onUp = () => {
      if (!isDragging.current) return
      isDragging.current = false
      try { localStorage.setItem(storageKey, JSON.stringify(posRef.current)) } catch {}
    }
    const onTouchMove = (e: TouchEvent) => {
      if (!isDragging.current) return
      e.preventDefault()
      const t = e.touches[0]
      setPos({ x: t.clientX - dragOffset.current.x, y: t.clientY - dragOffset.current.y })
    }
    const onTouchEnd = () => {
      if (!isDragging.current) return
      isDragging.current = false
      try { localStorage.setItem(storageKey, JSON.stringify(posRef.current)) } catch {}
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    document.addEventListener('touchmove', onTouchMove, { passive: false })
    document.addEventListener('touchend', onTouchEnd)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.removeEventListener('touchmove', onTouchMove)
      document.removeEventListener('touchend', onTouchEnd)
    }
  }, [storageKey])

  const onDragStart = (e: React.MouseEvent) => {
    isDragging.current = true
    dragOffset.current = { x: e.clientX - posRef.current.x, y: e.clientY - posRef.current.y }
    e.preventDefault()
  }

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0]
    isDragging.current = true
    dragOffset.current = { x: t.clientX - posRef.current.x, y: t.clientY - posRef.current.y }
  }

  const [maximized, setMaximized] = useState(false)

  const sizeStorageKey = `algoglide-widget-size:${id}`
  const [size, setSize] = useState<{ w: number; h: number } | null>(null)

  useEffect(() => {
    try {
      const saved = localStorage.getItem(sizeStorageKey)
      if (saved) setSize(JSON.parse(saved))
    } catch {}
  }, [sizeStorageKey])

  const isResizing = useRef<string | null>(null)
  const resizeStart = useRef({ x: 0, y: 0, w: 0, h: 0 })

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isResizing.current) return
      const dx = e.clientX - resizeStart.current.x
      const dy = e.clientY - resizeStart.current.y
      const dir = isResizing.current
      const newW = dir === "bottom" ? resizeStart.current.w : Math.max(200, resizeStart.current.w + dx)
      const newH = dir === "right" ? resizeStart.current.h : Math.max(120, resizeStart.current.h + dy)
      setSize({ w: newW, h: newH })
    }
    const onUp = () => {
      if (!isResizing.current) return
      isResizing.current = null
      setSize(prev => {
        if (prev) {
          try { localStorage.setItem(sizeStorageKey, JSON.stringify(prev)) } catch {}
        }
        return prev
      })
    }
    document.addEventListener("mousemove", onMove)
    document.addEventListener("mouseup", onUp)
    return () => {
      document.removeEventListener("mousemove", onMove)
      document.removeEventListener("mouseup", onUp)
    }
  }, [sizeStorageKey])

  const onResizeStart = (dir: string) => (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    isResizing.current = dir
    const el = (e.target as HTMLElement).closest('[data-floating-widget]') as HTMLElement | null
    resizeStart.current = {
      x: e.clientX,
      y: e.clientY,
      w: el?.offsetWidth ?? defaultWidth,
      h: el?.offsetHeight ?? defaultHeight,
    }
  }

  if (!mounted) return null

  // Docked mode: fill the panel slot, no portal
  const currentDock = docked[id]
  const isBottom = currentDock === 'bottom'
  if (currentDock) {
    return (
      <div className={cn(
        "flex flex-col bg-card h-full",
        isBottom ? "min-w-0" : "min-h-0"
      )}>
        <div className="group flex items-center justify-between h-8 px-3 border-b bg-muted/30 shrink-0 select-none">
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            {title}
          </span>
          <div className="flex items-center gap-0.5">
            <div className="invisible group-hover:visible flex items-center">
              {!isBottom && (
                <>
                  <DockBtn title="Move up" onClick={() => reorderWidget(id, 'up')}><ChevronUp size={11} /></DockBtn>
                  <DockBtn title="Move down" onClick={() => reorderWidget(id, 'down')}><ChevronDown size={11} /></DockBtn>
                </>
              )}
              {canDockLeft && currentDock !== 'left' && (
                <DockBtn title="Dock left" onClick={() => setDock(id, 'left')}><PanelLeft size={11} /></DockBtn>
              )}
              {currentDock !== 'bottom' && (
                <DockBtn title="Dock bottom" onClick={() => setDock(id, 'bottom')}><PanelBottom size={11} /></DockBtn>
              )}
              {canDockRight && currentDock !== 'right' && (
                <DockBtn title="Dock right" onClick={() => setDock(id, 'right')}><PanelRight size={11} /></DockBtn>
              )}
            </div>
            <button
              onClick={() => setDock(id, null)}
              title="Pop out to float"
              className="flex items-center justify-center w-5 h-5 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ExternalLink size={11} />
            </button>
            <button
              onClick={() => { setDock(id, null); setMaximized(true) }}
              title="Maximize"
              className="flex items-center justify-center w-5 h-5 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Maximize2 size={11} />
            </button>
            <button
              onClick={onClose}
              className="flex items-center justify-center w-5 h-5 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X size={12} />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col bg-card">
          {children}
        </div>
      </div>
    )
  }

  // Floating mode: portal to document.body
  const effectiveW = size?.w ?? Math.min(defaultWidth, window.innerWidth - 16)
  const effectiveH = fitContent ? undefined : (size?.h ?? defaultHeight)

  const widget = (
    <div
      data-floating-widget
      className={cn(
        "fixed z-[9999] flex flex-col border border-border bg-card shadow-xl overflow-hidden",
        maximized && "!inset-0 !w-full !h-full !border-0"
      )}
      style={maximized ? undefined : {
        left: pos.x,
        top: pos.y,
        width: effectiveW,
        ...(effectiveH ? { height: effectiveH } : { maxHeight: '85vh' }),
      }}
    >
      {/* Drag handle header */}
      <div
        onMouseDown={maximized ? undefined : onDragStart}
        onTouchStart={maximized ? undefined : onTouchStart}
        className={cn(
          "group flex items-center justify-between h-8 px-3 border-b bg-muted/30 shrink-0 select-none",
          !maximized && "cursor-grab active:cursor-grabbing"
        )}
      >
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          {title}
        </span>
        <div className="flex items-center gap-0.5">
          <div className="invisible group-hover:visible flex items-center">
            {!maximized && canDockLeft  && <DockBtn title="Dock left"   onClick={(e) => { e.stopPropagation(); setDock(id, 'left')   }}><PanelLeft   size={11} /></DockBtn>}
            {!maximized && <DockBtn title="Dock bottom" onClick={(e) => { e.stopPropagation(); setDock(id, 'bottom') }}><PanelBottom size={11} /></DockBtn>}
            {!maximized && canDockRight && <DockBtn title="Dock right"  onClick={(e) => { e.stopPropagation(); setDock(id, 'right')  }}><PanelRight  size={11} /></DockBtn>}
          </div>
          <button
            onClick={() => setMaximized(m => !m)}
            title={maximized ? "Restore" : "Maximize"}
            className="flex items-center justify-center w-5 h-5 text-muted-foreground hover:text-foreground transition-colors"
          >
            {maximized ? <Minimize2 size={11} /> : <Maximize2 size={11} />}
          </button>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-5 h-5 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={12} />
          </button>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {children}
      </div>
      {!fitContent && !maximized && (
        <>
          <div
            onMouseDown={onResizeStart("right")}
            className="absolute top-0 right-0 w-1 h-full cursor-ew-resize z-10 hover:bg-primary/20 transition-colors"
          />
          <div
            onMouseDown={onResizeStart("bottom")}
            className="absolute bottom-0 left-0 w-full h-1 cursor-ns-resize z-10 hover:bg-primary/20 transition-colors"
          />
          <div
            onMouseDown={onResizeStart("corner")}
            className="absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize z-20"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" className="absolute bottom-0.5 right-0.5 text-muted-foreground/40">
              <path d="M9 1L1 9M9 5L5 9M9 9L9 9" stroke="currentColor" strokeWidth="1" />
            </svg>
          </div>
        </>
      )}
    </div>
  )

  return createPortal(widget, document.body)
}

export function ExchangeLockedState() {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-3 p-6 text-center">
      <p className="text-xs text-muted-foreground">Connect your Binance account to unlock this widget.</p>
      <a
        href="/preferences#exchange"
        className="text-xs font-medium underline underline-offset-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        Connect exchange
      </a>
    </div>
  )
}
